import { FC, useCallback, useEffect, useState } from 'react';
import { Alert, Button, CircularProgress, Typography } from '@mui/material';
import { OpenInNew, Refresh } from '@mui/icons-material';
import { api } from 'services/api';
import { getPgErrorMessage } from 'services/api/paymentGateway/errors';
import { TPgMe, TPgSystemInfo } from 'services/api/paymentGateway/types';
import { PAYMENT_GATEWAY_HANGFIRE_URL } from 'services/constants/env';
import { PgFields } from '../common/PgFields';
import { PgPage } from '../common/PgPage';
import { PgPanel } from '../common/PgPanel';
import { PgStatusChip } from '../common/PgMiniTable';

const flag = (enabled: boolean) => (
	<PgStatusChip value={enabled ? 'active' : 'disabled'} label={enabled ? 'Enabled' : 'Disabled'} />
);

/** System (03 §4.12): version, environment, operation switches and configuration warnings. */
export const System: FC = () => {
	const [system, setSystem] = useState<TPgSystemInfo | null>(null);
	const [me, setMe] = useState<TPgMe | null>(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const load = useCallback(async () => {
		setLoading(true);
		setError(null);
		try {
			const [systemInfo, operator] = await Promise.all([
				api.paymentGateway.getSystem(),
				api.paymentGateway.getMe(),
			]);
			setSystem(systemInfo);
			setMe(operator);
		} catch (e) {
			setError(getPgErrorMessage(e, 'Failed to load the system information'));
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		void load();
	}, [load]);

	const hangfireUrl = system?.hangfireUrl || PAYMENT_GATEWAY_HANGFIRE_URL;

	return (
		<PgPage
			title="System"
			actions={
				<>
					<Button
						size="small"
						href={hangfireUrl}
						target="_blank"
						rel="noopener noreferrer"
						endIcon={<OpenInNew />}
					>
						Jobs (Hangfire)
					</Button>
					<Button
						size="small"
						onClick={() => void load()}
						disabled={loading}
						startIcon={loading ? <CircularProgress size={14} /> : <Refresh />}
					>
						Refresh
					</Button>
				</>
			}
		>
			{error && (
				<Alert severity="error" sx={{ mb: 2 }}>
					{error}
				</Alert>
			)}
			{system && (
				<>
					<PgPanel title="Gateway">
						<PgFields
							items={[
								['Version', system.version],
								['Environment', system.environment],
								['Public base URL', system.publicBaseUrl],
								['Hangfire', system.hangfireUrl],
							]}
						/>
					</PgPanel>
					<PgPanel title="Operations">
						<PgFields
							minWidth={200}
							items={[
								['Money movement (charges, refunds)', flag(system.operations.moneyMovementEnabled)],
								['Monthly cycle (recurring)', flag(system.operations.recurringEnabled)],
								['Webhook processing', flag(system.operations.webhookProcessingEnabled)],
								['Outbox dispatcher', flag(system.operations.outboxEnabled)],
							]}
						/>
						{!system.operations.moneyMovementEnabled && (
							<Alert severity="warning" sx={{ mt: 2 }}>
								Money movement is switched off in the gateway configuration: charges and refunds are
								refused.
							</Alert>
						)}
					</PgPanel>
					<PgPanel title={`Configuration warnings (${system.configWarnings.length})`}>
						{system.configWarnings.length ? (
							system.configWarnings.map((warning) => (
								<Alert key={warning} severity="warning" sx={{ mb: 1 }}>
									{warning}
								</Alert>
							))
						) : (
							<Typography variant="body2" color="text.secondary">
								No warnings
							</Typography>
						)}
					</PgPanel>
				</>
			)}
			{me && (
				<PgPanel title="You">
					<PgFields
						items={[
							['Name', me.name],
							['E-mail', me.email],
							['Subject', me.sub],
							['Payment Gateway admin', me.isPaymentGatewayAdmin ? 'Yes' : 'No'],
						]}
					/>
				</PgPanel>
			)}
		</PgPage>
	);
};
